import React from "react";
import { useNavigate } from "react-router-dom";

import Icons from "assets";
import { Box } from "ui/box";
import { Chip } from "ui/chip";
import { Button } from "ui/button";
import { TableLoader } from "ui/data-grid";
import { TextButton } from "ui/text-button";
import { TextField } from "ui/text-field";
import { Typography } from "ui/typography";
import FormPageLayout from "containers/admin/clients/client/components/form-page-layout/form-page-layout";
import { getProgramTypeLabel } from "containers/admin/clients/client/campaign-templates-page/campaign-template.constants";
import { CampaignTemplateSummaryPanel } from "containers/admin/clients/client/campaign-templates-page/campaign-template-summary-panel";
import { useCampaignTemplateForm } from "containers/admin/clients/client/campaign-templates-page/use-campaign-template-form";

import { getClientPaths } from "utilities/app-routes";
import { formatDateTime } from "utilities/date";

const FORM_ID = "campaign-template-form";

const CampaignTemplateDetailsPage = () => {
  const navigate = useNavigate();
  const paths = getClientPaths({ clientId: undefined });
  const {
    template,
    tabs,
    sidebarItems,
    isTemplateLoading,
    isSchemaLoading,
    isEditing,
    setIsEditing,
    hasChanges,
    isSaveDisabled,
    tabRevisions,
    getTabValues,
    handleTabValuesChange,
    handleDiscard,
    handleSubmit,
    missingGenerationFieldsDialog,
    draftName,
    handleNameChange,
    isSaving,
    isTemplateIncomplete,
  } = useCampaignTemplateForm();

  if (isTemplateLoading || (template?.programType && isSchemaLoading)) {
    return <TableLoader isLoading fullScreen />;
  }

  if (!template || !tabs.length) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography variant="body1" sx={{ color: "grey.600" }}>
          Campaign brief is not available.
        </Typography>
        <Button variant="outlined" onClick={() => navigate(paths.clientCampaignTemplatesPath)}>
          Back to campaign briefs
        </Button>
      </Box>
    );
  }

  const breadcrumbs = [
    { label: "Admin workspace", active: false as const },
    { label: "Campaign briefs", href: paths.clientCampaignTemplatesPath },
  ];

  const handleDraftNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleNameChange(event.target.value);
  };

  const renderPageHeader = (editActions: React.ReactNode) => (
    <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexShrink: 0 }}>
      <Box sx={{ display: "flex", minWidth: 0, maxWidth: 640, flex: 1, flexDirection: "column", gap: 1.5 }}>
        {isEditing ? (
          <TextField
            name="campaign-template-title"
            aria-label="Campaign brief title"
            value={draftName}
            onChange={handleDraftNameChange}
            disabled={isSaving}
            inputSx={(theme) => ({
              height: "auto",
              border: "1px solid",
              borderColor: theme.palette.divider,
              backgroundColor: theme.palette.common.white,
              paddingX: 1.5,
              paddingY: 0.75,
              fontSize: "0.875rem",
              lineHeight: "1.25rem",
            })}
          />
        ) : (
          <Typography variant="h3" component="h1" sx={{ color: "secondary.main" }}>
            {template.name}
          </Typography>
        )}
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            {template.programType && (
              <Chip
                size="small"
                variant="outlined"
                label={getProgramTypeLabel(template.programType)}
              />
            )}
            {isTemplateIncomplete && (
              <Chip
                size="small"
                variant="outlined"
                icon={<Icons.Edit />}
                label="Incomplete brief"
              />
            )}
          </Box>
          {template.strategy && (
            <TextButton
              endIcon={<Icons.Forward />}
              onClick={() => window.open(template.strategy, "_blank", "noopener,noreferrer")}
              sx={{ whiteSpace: "nowrap" }}
            >
              Program strategy
            </TextButton>
          )}
          <Typography
            variant="body2"
            component="span"
            sx={(theme) => ({
              lineHeight: theme.typography.body1.lineHeight,
              color: theme.palette.text.secondary,
              whiteSpace: "nowrap",
              flexShrink: 0,
            })}
          >
            Last updated{" "}
            <Typography component="span" variant="inherit" sx={{ color: "grey.900", fontWeight: 600 }}>
              {formatDateTime(template.lastModifiedDate)}
            </Typography>
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: "flex", flexShrink: 0, alignItems: "center", gap: 1.5 }}>
        {editActions}
        {!isEditing && (
          <CampaignTemplateSummaryPanel
            source={{ type: "template", templateId: template.id ?? "" }}
          />
        )}
      </Box>
    </Box>
  );

  return (
    <>
      {missingGenerationFieldsDialog}
      <FormPageLayout
        formId={FORM_ID}
        sidebarSectionId="campaign-template-details-nav"
        tabs={tabs}
        sidebarItems={sidebarItems}
        customBreadcrumbs={breadcrumbs}
        renderPageHeader={renderPageHeader}
        rootClassName="flex flex-col flex-1 min-h-0 pt-3 gap-4"
        getTabValues={getTabValues}
        onTabValuesChange={handleTabValuesChange}
        tabRevisions={tabRevisions}
        isSaveDisabled={isSaveDisabled}
        isDiscardDisabled={!hasChanges}
        isSaving={isSaving}
        isEditing={isEditing}
        onIsEditingChange={setIsEditing}
        editButtonLabel="Edit"
        onSubmit={handleSubmit}
        onDiscard={handleDiscard}
      />
    </>
  );
};

export default CampaignTemplateDetailsPage;
